import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { CreditCard, LayoutDashboard, LogOut, Radio, Settings as SettingsIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useTransactions } from '@/lib/queries';
import { NOUN, VERB, plural, pluralize } from '@/lib/plural';
import { TxStatus } from '@shared/types';

/**
 * The three things a trader does. Верификация is deliberately absent: it is a
 * once-ever task, so it is reached from the panel on Обзор and only while it
 * still matters, rather than costing a permanent slot down here.
 *
 * `CreditCard` is also used inside PaymentCard for the destination-card line —
 * which is the point: the icon means "a bank card" in both places.
 */
const NAV = [
  { to: '/dashboard', label: 'Обзор', icon: LayoutDashboard },
  { to: '/traffic', label: 'Трафик', icon: Radio },
  { to: '/cards', label: 'Реквизиты', icon: CreditCard },
] as const;

/** Desktop only. On a phone this is the gear in the top bar. */
const SETTINGS_NAV = { to: '/settings', label: 'Настройки', icon: SettingsIcon } as const;

const RAIL_NAV = [...NAV, {to:'/reports',label:'Отчёты',icon:LayoutDashboard}, SETTINGS_NAV];

/**
 * Fixed rail on desktop, bottom tab bar on a phone.
 *
 * The bar replaced a hamburger sheet, for two reasons that both cost real
 * money. The sheet opened in flow rather than over the page, so tapping the
 * menu shoved the balance and the payment list off-screen; and the pending
 * badge lived *inside* it, which meant a payment arriving while the trader was
 * on another screen was invisible until they went looking. On the bar the count
 * is always in view, and every destination is one thumb-reach away.
 */
export default function DashboardLayout() {
  const signOut = useAuthStore((state) => state.signOut);
  const trader = useAuthStore((state) => state.trader);

  // The pending count is a live badge on the Traffic link, so a payment that
  // arrives while the trader is on Settings is still visible.
  const { data: transactions } = useTransactions({ limit: 50, live: true });
  const pendingCount =
    transactions?.filter((tx) => tx.status === TxStatus.Pending).length ?? 0;

  // Announce only an increase — a falling count means the trader just dealt
  // with something and does not need telling.
  const [arrivalNotice, setArrivalNotice] = useState('');
  const previousPending = useRef(pendingCount);

  useEffect(() => {
    if (pendingCount > previousPending.current) {
      const added = pendingCount - previousPending.current;
      setArrivalNotice(
        `${plural(added, VERB.arrived)} ${pluralize(added, NOUN.payment)}. ` +
          `Сейчас ${plural(pendingCount, VERB.awaits)} подтверждения ${pluralize(pendingCount, NOUN.payment)}.`,
      );
    }
    previousPending.current = pendingCount;
  }, [pendingCount]);

  return (
    <div className="min-h-screen bg-canvas">
      <a href="#main" className="skip-link">
        Перейти к содержимому
      </a>

      {/* A payment arriving while the trader is on another screen is otherwise a
          silent, visual-only badge change. Announced only when the count rises. */}
      <p role="status" aria-live="polite" className="sr-only">
        {arrivalNotice}
      </p>

      {/* Mobile bar: identity, plus the one destination that lost its tab.
          Настройки is a place you visit occasionally and leave, so it sits up
          here — reachable from every screen — instead of spending a quarter of
          the thumb bar. The theme control stays inside it with the other
          preferences, as it has since the sheet was removed. */}
      <header className="safe-topbar sticky top-0 z-nav flex items-center justify-between gap-3 border-b border-line bg-surface lg:hidden">
        <Link to="/dashboard" className="focus-ring tap-target rounded-sm no-underline">
          <span className="text-base font-semibold tracking-tight text-fg">p3p-pay</span>
        </Link>

        <div className="flex min-w-0 items-center gap-3">
          <span className="num truncate text-2xs uppercase tracking-wide text-fg-muted">
            Трейдер
          </span>
          {/* `tap-target` rather than a real 44px box: the only other control
              in this bar is the wordmark at the opposite edge, so nothing can
              have its taps stolen. `-m-1 p-1` gives the focus ring room to
              breathe without adding to the bar's height — the bar has to stay
              ~44px so the expanded hit areas keep clear of the page beneath. */}
          <NavLink
            to={SETTINGS_NAV.to}
            aria-label={SETTINGS_NAV.label}
            className={({ isActive }) =>
              cn(
                'focus-ring tap-target -m-1 flex shrink-0 items-center justify-center rounded-sm p-1',
                'transition-colors duration-fast',
                isActive ? 'text-fg' : 'text-fg-muted',
              )
            }
          >
            <SettingsIcon size={20} strokeWidth={1.5} aria-hidden="true" />
          </NavLink>
        </div>
      </header>

      <div className="lg:grid lg:grid-cols-shell">
        {/* Rail — desktop only; `hidden` keeps it out of the accessibility tree
            on a phone so the tab bar is the single navigation landmark. */}
        <nav
          aria-label="Основная навигация"
          className="hidden border-line bg-surface lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:border-r"
        >
          <div className="flex items-baseline gap-2 px-5 py-6">
            <span className="text-md font-semibold tracking-tight text-fg">p3p-pay</span>
            <span className="num text-2xs uppercase tracking-wide text-fg-muted">Трейдер</span>
          </div>

          {/* The rail carries Настройки as a fourth row: there is room for it
              here, and dropping it would leave a desktop trader with only the
              gear that the phone layout gets. */}
          <ul className="flex-1 space-y-0.5 px-3">
            {RAIL_NAV.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      'focus-ring group flex items-center gap-2.5 rounded-md px-2.5 py-1.5',
                      'text-base no-underline transition-colors duration-fast',
                      isActive
                        ? 'bg-sunken font-medium text-fg'
                        : 'font-normal text-fg-secondary hover:bg-sunken hover:text-fg',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        size={15}
                        strokeWidth={1.5}
                        aria-hidden="true"
                        className={isActive ? 'text-fg' : 'text-fg-faint'}
                      />
                      <span className="min-w-0 flex-1 truncate">{label}</span>
                      {to === '/traffic' && pendingCount > 0 ? (
                        <span
                          className="num shrink-0 rounded-sm border border-warn-500 bg-warn-50 px-1 text-2xs font-semibold text-warn-700"
                          aria-label={`${pluralize(pendingCount, NOUN.payment)} ${plural(pendingCount, VERB.awaits)} подтверждения`}
                        >
                          {pendingCount}
                        </span>
                      ) : null}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="border-t border-line px-3 py-3">
            <div className="px-2.5 pb-3">
              <p className="eyebrow">Вы вошли как</p>
              <p className="num mt-1 truncate text-xs text-fg-secondary" title={trader?.email}>
                {trader?.email ?? '—'}
              </p>
            </div>

            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={signOut}
                className={cn(
                  'focus-ring flex items-center gap-2.5 rounded-md px-2.5 py-1.5',
                  'text-base text-fg-secondary transition-colors duration-fast',
                  'hover:bg-danger-50 hover:text-danger-700',
                )}
              >
                <LogOut size={15} strokeWidth={1.5} aria-hidden="true" />
                Выйти
              </button>
              <ThemeToggle />
            </div>
          </div>
        </nav>

        {/* Content column */}
        <main id="main" className="safe-x min-w-0">
          <div className="safe-content-bottom mx-auto w-full max-w-content px-4 pt-6 sm:px-6 lg:px-10 lg:pt-10">
            <Outlet />
          </div>
        </main>
      </div>

      <MobileTabBar pendingCount={pendingCount} />
    </div>
  );
}

/**
 * Three destinations, always visible, always within thumb reach.
 *
 * Three rather than four because Настройки moved to the top bar: at 320px a
 * quarter column left «Верификация» 72px of label, and the widest label here now
 * gets 98px in a third of the same bar.
 *
 * The active tab is marked three ways — a top edge, a weight change and colour —
 * because on a phone held at arm's length outdoors, colour alone is the first
 * signal to disappear.
 */
function MobileTabBar({ pendingCount }: { pendingCount: number }) {
  return (
    <nav
      aria-label="Основная навигация"
      className="safe-tabbar fixed inset-x-0 bottom-0 z-nav border-t border-line bg-surface lg:hidden"
    >
      <ul className="grid grid-cols-3">
        {NAV.map(({ to, label, icon: Icon }) => (
          <li key={to} className="min-w-0">
            <NavLink
              to={to}
              className={({ isActive }) =>
                cn(
                  'focus-ring relative flex min-h-[var(--tabbar-h)] w-full flex-col items-center',
                  'justify-center gap-1 rounded-sm px-1 py-1.5 text-2xs no-underline',
                  'transition-colors duration-fast',
                  'before:absolute before:inset-x-2 before:top-0 before:h-[2px] before:content-[""]',
                  isActive
                    ? 'font-semibold text-fg before:bg-fg'
                    : 'font-normal text-fg-muted before:bg-transparent',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative flex shrink-0">
                    <Icon
                      size={20}
                      strokeWidth={isActive ? 2 : 1.5}
                      aria-hidden="true"
                      className={isActive ? 'text-fg' : 'text-fg-muted'}
                    />
                    {to === '/traffic' && pendingCount > 0 ? (
                      <span
                        className={cn(
                          'num absolute -right-2.5 -top-1.5 min-w-[1.1rem] rounded-full',
                          'border border-warn-500 bg-warn-50 px-1 text-center text-2xs',
                          'font-semibold leading-[0.95rem] text-warn-700',
                        )}
                        aria-label={`${pluralize(pendingCount, NOUN.payment)} ${plural(pendingCount, VERB.awaits)} подтверждения`}
                      >
                        {pendingCount > 99 ? '99+' : pendingCount}
                      </span>
                    ) : null}
                  </span>
                  <span className="w-full truncate text-center leading-tight">{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
